// Hierarchical Tree Data
// Represents hierarchical data structure for tree-based visualizations
// Data structure matches the HierarchicalTreeChart component's TreeNode requirements

import { DimIdsFilter } from 'components/pages/ProfitPoolOverview/apiUtils';
import { kebabCase, round } from 'lodash';
import { formatNumWithKMBAlt2 } from 'utils/helperMethods';

export interface HierarchicalTreeNode {
    id?: string;
    name: string; // Displayed as label (top of node)
    label: string; // Displayed as price/value (e.g., "$1.2 M")
    currency?: string; // Currency symbol (e.g., "$")
    variableValue: number; // Percentage as decimal (0-1, e.g., 0.12 for 12%)
    status: 'PNP' | 'R' | 'Y' | 'G' | 'DI'; // Color status
    rate: number; // Progress bar percentage as decimal (0-1)
    variableName?: string; // Variable name text
    variableUp?: boolean; // Triangle direction (true = up, false = down)
    children?: HierarchicalTreeNode[];
    // Metadata for hierarchy navigation
    attributeType?: string;
    dimension?: string;
    attributeExId?: string;
    isClickable?: boolean;
    nextAttributeType?: string;
    nextDimension?: string;
    nextIsAttribute?: boolean;
    dimIdsFilter?: DimIdsFilter;
}

// Total value for example data: $12M
const TOTAL_VALUE = 12000000;

// Format number to display format (e.g., 1200000 -> "1.2 M")
export const formatValue = (value: number): string => {
    if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)} M`;
    } else if (value >= 1000) {
        return `${(value / 1000).toFixed(1)} K`;
    }
    return value.toString();
};

// Calculate percentage of total
const calculatePercentage = (value: number): number => {
    return value / TOTAL_VALUE;
};

// Example dataset for hierarchical tree visualization
// Based on a hierarchical structure with multiple levels
export const hierarchicalTreeData: HierarchicalTreeNode = {
    id: 'brand-family',
    name: 'Brand Family',
    label: formatValue(TOTAL_VALUE), // "$12.0 M"

    variableValue: 1.0, // 100%
    status: 'PNP', // Blue
    rate: 1.0, // 100% progress bar
    variableName: 'Total',
    variableUp: true,
    children: [
        {
            id: 'brand1',
            name: 'Brand 1',
            label: formatValue(1200000), // "$1.2 M"
            variableValue: 0.12, // 12% as shown in image tooltip
            status: 'PNP', // Blue
            rate: calculatePercentage(1200000), // Progress bar based on actual value
            variableName: 'Share',
            variableUp: true,
            children: [
                {
                    id: 'brand112',
                    name: 'Sub Brand 1',
                    label: formatValue(1200000), // "$1.2 M"
                    variableValue: 0.12, // 12% as shown in image tooltip
                    status: 'PNP', // Blue
                    rate: calculatePercentage(1200000), // Progress bar based on actual value
                    variableName: 'Share',
                    variableUp: true,
                    children: [
                        {
                            id: 'brand1123',
                            name: 'Pack Brand 1',
                            label: formatValue(1200000), // "$1.2 M"
                            variableValue: 0.12, // 12% as shown in image tooltip
                            status: 'PNP', // Blue
                            rate: calculatePercentage(1200000), // Progress bar based on actual value
                            variableName: 'Share',
                            variableUp: true,
                        },
                    ],
                },
            ],
        },
        {
            id: 'brand2',
            name: 'Brand 2',
            label: formatValue(2400000), // "$2.4 M"

            variableValue: calculatePercentage(2400000), // 20%
            status: 'PNP', // Blue
            rate: calculatePercentage(2400000),
            variableName: 'Share',
            variableUp: true,
        },
        {
            id: 'brand3',
            name: 'Brand 3',
            label: formatValue(1800000), // "$1.8 M"

            variableValue: calculatePercentage(1800000), // 15%
            status: 'PNP', // Blue
            rate: calculatePercentage(1800000),
            variableName: 'Share',
            variableUp: true,
        },
        {
            id: 'brand4',
            name: 'Brand 4',
            label: formatValue(2400000), // "$2.4 M"

            variableValue: calculatePercentage(2400000), // 20%
            status: 'PNP', // Blue
            rate: calculatePercentage(2400000),
            variableName: 'Share',
            variableUp: true,
        },
        {
            id: 'brand5',
            name: 'Brand 5',
            label: formatValue(2100000), // "$2.1 M"

            variableValue: calculatePercentage(2100000), // 17.5%
            status: 'PNP', // Blue
            rate: calculatePercentage(2100000),
            variableName: 'Share',
            variableUp: true,
        },
        {
            id: 'brand6',
            name: 'Brand 6',
            label: formatValue(2100000), // "$2.1 M"

            variableValue: calculatePercentage(2100000), // 17.5%
            status: 'PNP', // Blue
            rate: calculatePercentage(2100000),
            variableName: 'Share',
            variableUp: true,
        },
    ],
};

// Legacy interface and data (kept for backward compatibility if needed)
export interface HierarchicalNode {
    id: string;
    label: string;
    value: number;
}

export interface HierarchicalEdge {
    source: string;
    target: string;
    value: number;
}

export interface HierarchicalDataSet {
    nodes: HierarchicalNode[];
    edges: HierarchicalEdge[];
}

// Example dataset for edge-based representation
export const dummyHierarchicalDataSet: HierarchicalDataSet = {
    nodes: [
        {
            id: 'brand-family',
            label: 'Brand Family',
            value: TOTAL_VALUE,
        },
        {
            id: 'brand1',
            label: 'Brand 1',
            value: 1200000,
        },
        {
            id: 'brand2',
            label: 'Brand 2',
            value: 2400000,
        },
        {
            id: 'brand3',
            label: 'Brand 3',
            value: 1800000,
        },
        {
            id: 'brand4',
            label: 'Brand 4',
            value: 2400000,
        },
        {
            id: 'brand5',
            label: 'Brand 5',
            value: 2100000,
        },
        {
            id: 'brand6',
            label: 'Brand 6',
            value: 2100000,
        },
    ],
    edges: [
        { source: 'brand-family', target: 'brand1', value: 1200000 },
        { source: 'brand-family', target: 'brand2', value: 2400000 },
        { source: 'brand-family', target: 'brand3', value: 1800000 },
        { source: 'brand-family', target: 'brand4', value: 2400000 },
        { source: 'brand-family', target: 'brand5', value: 2100000 },
        { source: 'brand-family', target: 'brand6', value: 2100000 },
    ],
};

// Helper functions for creating hierarchical tree nodes dynamically

/**
 * Creates a tree node from the selectedBarForViewBreakdown (from WaterFall chart)
 */
export const createNodeFromSelection = (
    selection: any,
    kpiKey: string = 'profit_absolute',
    selectedSubFilter: any,
): HierarchicalTreeNode | null => {
    if (!selection) return null;

    const value = selection[kpiKey];

    const absValue = Array.isArray(value)
        ? Number(Math.abs(value[1] - value[0])?.toFixed(2))
        : Number(Math.abs(value || 0)?.toFixed(2));
    return {
        id: `selection-${selection.attribute_ex_id || selection.attributeDesc}`,
        name: selection.attributeDesc || 'Selection',
        label: formatValue(absValue),
        variableValue: 1.0,
        status: 'PNP',
        rate: 1.0,
        variableName: 'Selected',
        variableUp: true,
        attributeType: selectedSubFilter?.dataKey,
        dimension: selectedSubFilter?.dimension,
        attributeExId: selection.attribute_ex_id,
        isClickable: true,
        children: [],
    };
};

/**
 * Creates child nodes from API response data
 */
export const createChildNodesFromApiData = (
    apiData: any[],
    kpiKey: string = 'profit_absolute',
    attributeType: string,
    dimension: string,
    parentTotal?: number,
    parentId?: string,
    parentAttributeExId?: string,
    nextIsAttribute?: boolean,
    dimIdsFilter?: DimIdsFilter,
): HierarchicalTreeNode[] => {
    if (!apiData || !apiData.length) return [];

    // Calculate total for percentage calculations
    const total =
        parentTotal ||
        apiData.reduce((acc, item) => {
            const val = item.kpi_values?.[kpiKey] || 0;
            return acc + Math.abs(val);
        }, 0);

    // Create a unique parent identifier for ID generation
    const parentIdentifier = parentId || parentAttributeExId || 'root';

    return apiData.map((item, index) => {
        const kpiValue = item.kpi_values?.[kpiKey] || 0;
        const absValue = Number(Math.abs(kpiValue).toFixed(2));
        const percentage = total > 0 ? absValue / total : 0;

        // Generate unique ID with parent context: parentId-attributeType-dimension-attributeExId-index
        const childId = item.attribute_ex_id
            ? `${parentIdentifier}-${attributeType}-${dimension}-${item.attribute_ex_id}`
            : `${parentIdentifier}-${attributeType}-${dimension}-${index}`;

        return {
            id: kebabCase(childId),
            name: item.attribute_desc || `Item ${index + 1}`,
            label: formatNumWithKMBAlt2(kpiValue, 2, 2),
            kpiValue,
            variableValue: round(percentage, 2),
            status: kpiValue > 0 ? ('PNP' as const) : ('R' as const),
            rate: round(percentage, 2),
            variableName: 'Share',
            variableUp: kpiValue >= 0,
            attributeType,
            dimension,
            attributeExId: item.attribute_ex_id,
            attributeDesc: item.attribute_desc,
            isClickable: true,
            isAttribute: nextIsAttribute,
        };
    });
};

/**
 * Adds children to a parent node by matching ID (preferred) or name
 */

export const addChildrenToNode = (
    parentNode: HierarchicalTreeNode,
    parentId: string | undefined,
    parentName: string | undefined,
    children: HierarchicalTreeNode[],
): HierarchicalTreeNode => {
    // Match by ID first (more reliable), then fall back to name
    const isMatch = parentId ? parentNode.id === parentId : parentName ? parentNode.name === parentName : false;

    // If this is the parent node, add children
    if (isMatch) {
        return {
            ...parentNode,
            // children: [...(parentNode.children || []), ...children],
            children: [...children],
        };
    }

    // Recursively search in children
    if (parentNode.children && parentNode.children.length > 0) {
        return {
            ...parentNode,
            children: parentNode.children.map((child) => addChildrenToNode(child, parentId, parentName, children)),
        };
    }

    return parentNode;
};

/**
 * Finds and returns the next hierarchy level info from the dimension list
 */
export const getNextHierarchyInfo = (
    currentAttributeType: string,
    currentDimension: string,
    dimensionList: any[],
): { nextAttributeType: string; nextDimension: string; nextIsAttribute?: boolean } | null => {
    if (!dimensionList || !dimensionList.length) return null;

    // Flatten the dimension list to get all attribute types in order
    const flatList: { key: string; dimension: string; value: number; isAttribute?: boolean }[] = [];

    dimensionList.forEach((dim) => {
        if (dim.attribute?.length > 0) {
            dim.attribute.forEach((attr: any) => {
                flatList.push({
                    key: attr.name,
                    dimension: attr.dimensionReference,
                    value: flatList.length + 1,
                    isAttribute: attr.isAttribute, // Add this
                });
            });
        }
    });

    // Find the current attribute in the flat list
    const currentIndex = flatList.findIndex(
        (item) => item.key.toLowerCase() === currentAttributeType.toLowerCase() && item.dimension === currentDimension,
    );

    // Return next item if it exists and is in the same dimension
    if (currentIndex >= 0 && currentIndex < flatList.length - 1) {
        const nextItem = flatList[currentIndex + 1];
        // Only return next hierarchy if it's in the same dimension
        if (nextItem.dimension === currentDimension) {
            return {
                nextAttributeType: nextItem.key,
                nextDimension: nextItem.dimension,
                nextIsAttribute: nextItem.isAttribute, // Add this
            };
        }
    }

    return null;
};

/**
 * Truncates the tree at a specific depth level (0-indexed)
 * depth 0 = root only, depth 1 = root + children, etc.
 * @param node - The root node of the tree
 * @param maxDepth - Maximum depth to keep (0-indexed)
 * @param currentDepth - Current depth in recursion (internal use)
 * @returns New tree truncated at specified depth
 */
export const truncateTreeAtDepth = (
    node: HierarchicalTreeNode,
    maxDepth: number,
    currentDepth: number = 0,
): HierarchicalTreeNode => {
    // If we're at the max depth, remove children
    if (currentDepth >= maxDepth) {
        const { children, ...nodeWithoutChildren } = node;
        return { ...nodeWithoutChildren, children: [] };
    }

    // If node has children, recursively truncate them
    if (node.children && node.children.length > 0) {
        return {
            ...node,
            children: node.children.map((child) => truncateTreeAtDepth(child, maxDepth, currentDepth + 1)),
        };
    }

    return { ...node };
};

/**
 * Gets the current depth of the tree
 * @param node - The root node
 * @returns Maximum depth of the tree (0 for just root)
 */
export const getTreeDepth = (node: HierarchicalTreeNode): number => {
    if (!node.children || node.children.length === 0) {
        return 0;
    }
    return 1 + Math.max(...node.children.map((child) => getTreeDepth(child)));
};

/**
 * Removes children from all nodes at a specific attribute type level
 * @param node - The root node
 * @param attributeType - The attribute type to truncate at
 * @returns New tree with children removed from nodes of specified attribute type
 */
export const truncateTreeAtAttributeType = (
    node: HierarchicalTreeNode,
    attributeType: string,
): HierarchicalTreeNode => {
    // If this node matches the attribute type, remove its children
    if (node.attributeType?.toLowerCase() === attributeType.toLowerCase()) {
        const { children, ...nodeWithoutChildren } = node;
        return { ...nodeWithoutChildren, children: [] };
    }

    // If node has children, recursively process them
    if (node.children && node.children.length > 0) {
        return {
            ...node,
            children: node.children.map((child) => truncateTreeAtAttributeType(child, attributeType)),
        };
    }

    return { ...node };
};

/**
 * Gets all unique attribute types in the tree (in order of depth)
 * @param node - The root node
 * @param depth - Current depth (internal use)
 * @returns Array of attribute types in order of hierarchy
 */
export const getTreeAttributeTypes = (
    node: HierarchicalTreeNode,
    result: { attributeType: string; dimension: string; depth: number }[] = [],
    depth: number = 0,
): { attributeType: string; dimension: string; depth: number }[] => {
    // Add this node's attribute type if not already in result
    if (node.attributeType && node.dimension) {
        const exists = result.some((r) => r.attributeType === node.attributeType && r.dimension === node.dimension);
        if (!exists) {
            result.push({
                attributeType: node.attributeType,
                dimension: node.dimension,
                depth,
            });
        }
    }

    // Process children
    if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
            getTreeAttributeTypes(child, result, depth + 1);
        });
    }

    return result;
};

/**
 * Gets all leaf nodes at a specific attribute type level
 * Leaf nodes are nodes that have no children or whose children are at a different attribute type
 * @param node - The root node
 * @param attributeType - The attribute type to find leaf nodes for
 * @returns Array of leaf nodes at the specified attribute type level
 */

export const getLeafNodesAtAttributeType = (
    node: HierarchicalTreeNode | any,
    attributeType: string,
): HierarchicalTreeNode[] => {
    const result: HierarchicalTreeNode[] = [];

    const traverse = (currentNode: HierarchicalTreeNode) => {
        // Check if this node matches the attribute type
        if (currentNode.attributeType?.toLowerCase() === attributeType.toLowerCase()) {
            // Check if it's a leaf (no children or children are empty)
            result.push(currentNode);
        }

        // Continue traversing children
        if (currentNode.children && currentNode.children.length > 0) {
            currentNode.children.forEach((child) => traverse(child));
        }
    };

    traverse(node);
    return result;
};

/**
 * Gets all nodes at a specific attribute type level (regardless of whether they have children)
 * @param node - The root node
 * @param attributeType - The attribute type to find nodes for
 * @returns Array of all nodes at the specified attribute type level
 */
export const getAllNodesAtAttributeType = (
    node: HierarchicalTreeNode,
    attributeType: string,
): HierarchicalTreeNode[] => {
    const result: HierarchicalTreeNode[] = [];

    const traverse = (currentNode: HierarchicalTreeNode) => {
        // Check if this node matches the attribute type
        if (currentNode.attributeType?.toLowerCase() === attributeType.toLowerCase()) {
            result.push(currentNode);
        }

        // Continue traversing children
        if (currentNode.children && currentNode.children.length > 0) {
            currentNode.children.forEach((child) => traverse(child));
        }
    };

    traverse(node);
    return result;
};

/**
 * Removes all children from nodes at a specific attribute type level
 * @param node - The root node
 * @param attributeType - The attribute type whose children should be removed
 * @returns New tree with children removed from nodes of specified attribute type
 */
export const removeChildrenAtAttributeType = (
    node: HierarchicalTreeNode,
    attributeType: string,
): HierarchicalTreeNode => {
    // If this node matches the attribute type, remove its children
    if (node.attributeType?.toLowerCase() === attributeType.toLowerCase()) {
        return { ...node, children: [] };
    }

    // If node has children, recursively process them
    if (node.children && node.children.length > 0) {
        return {
            ...node,
            children: node.children.map((child) => removeChildrenAtAttributeType(child, attributeType)),
        };
    }

    return { ...node };
};

export function trimBelowDepth(node: HierarchicalTreeNode | any, maxDepth: number, depth = 0) {
    if (!node) return node;

    // If we are at the max depth, cut off children
    if (depth >= maxDepth) {
        return { ...node, children: [] };
    }
    return {
        ...node,
        children: node.children?.map((child: HierarchicalTreeNode | any) => trimBelowDepth(child, maxDepth, depth + 1)),
    };
}

export function findPath(node: any, targetId: string, path: any[] = []) {
    if (!node) return null;

    const nextPath = [...path, node];

    if (node.id === targetId) {
        return nextPath;
    }

    if (Array.isArray(node.children)) {
        for (const child of node.children) {
            const result: any = findPath(child, targetId, nextPath);
            if (result) return result;
        }
    }

    return null;
}

export function countNodes(node: HierarchicalTreeNode | any) {
    if (!node) return 0;

    let count = 1; // count self
    if (node.children) {
        for (const child of node.children) {
            count += countNodes(child);
        }
    }
    return count;
}

export const checkIfNullAttributePresent = (arr: any[]) => {
    let nullExists = false;
    for (let i = 0; i < arr.length; i++) {
        if (!arr[i].value) nullExists = true;
    }
    return nullExists;
};

const formatWord = (word: string) => {
    return word.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

export const formatHierarchicalTreeData = (tree: HierarchicalTreeNode | null): any[] => {
    if (!tree) return [];

    const rows: any[] = [];

    const traverse = (node: HierarchicalTreeNode, level: number = 0) => {
        const indent = '  '.repeat(level);
        rows.push([
            `${indent}${node.name || ''}`,
            node.label || '',
            node.attributeType && typeof node.attributeType === 'string' ? formatWord(node.attributeType) : '',
            `${((node.variableValue || 0) * 100).toFixed(2)}%`,
            node.variableName || '',
        ]);

        if (node.children && node.children.length > 0) {
            node.children.forEach((child) => traverse(child, level + 1));
        }
    };

    traverse(tree);
    return rows;
};
